import { BenefitType } from '@zorro/clients';
import { Stack } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';

import { EnrollmentAction } from '../../../EmployeePageHeader';
import { EmployeePagePlansBenefitCard } from './EmployeePagePlansBenefitCard';
import { EmployeePagePlansBenefitHeader } from './EmployeePagePlansBenefitHeader';
import { EmployeePagePlansEmptyBenefitCard } from './EmployeePagePlansEmptyBenefitCard';

type Props = {
  onEnrollmentTabActionChange: (action?: EnrollmentAction) => void;
  onboardingPeriodId: string;
  employerId: string;
  employeeId: string;
  isDon: boolean;
};

export const EmployeePagePlansElectionSubmitted = ({
  onEnrollmentTabActionChange,
  onboardingPeriodId,
  employerId,
  employeeId,
  isDon,
}: Props) => {
  const { data: benefits, isFetching } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });
  const { data: onboardingPeriodStatuses } = useMonolithQuery({
    method: 'enrollmentReportsControllerFetchSingleOnboardingPeriodStatuses',
    params: [onboardingPeriodId],
  });

  const medicalBenefit = benefits?.majorMedicalBenefit;
  const medicalPlan = medicalBenefit?.plan;
  const dentalBenefits = benefits?.dentalBenefits ?? [];
  const visionBenefits = benefits?.visionBenefits ?? [];

  if (!medicalPlan || !onboardingPeriod || isFetching) {
    return null;
  }

  const { isActive, enrollmentIsDentalEnabled, enrollmentIsVisionEnabled } =
    onboardingPeriod;

  return (
    <Stack w="100%" pt="md">
      {(enrollmentIsDentalEnabled || enrollmentIsVisionEnabled) && (
        <EmployeePagePlansBenefitHeader
          benefitType={BenefitType.MAJOR_MEDICAL}
          onboardingPeriodId={onboardingPeriodId}
        />
      )}
      <EmployeePagePlansBenefitCard
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
        benefitId={medicalBenefit.id}
        memberId={medicalBenefit.memberId}
        planId={medicalPlan.externalID}
        benefitType={BenefitType.MAJOR_MEDICAL}
        coverageChange={onboardingPeriodStatuses?.coverageChange}
        insuredPeople={medicalBenefit.insuredPeople}
        benefitsSummaryUrl={medicalPlan.benefitsSummaryUrl}
        status={medicalBenefit.status}
        carrierName={medicalPlan.carrierName}
        selfEnroll={medicalPlan.selfEnroll}
        planMarket={medicalPlan.planMarket}
        policyType={medicalPlan.policyType}
        premium={medicalPlan.premium}
        name={medicalPlan.name}
        isCombinedPlan={medicalPlan.isCombinedPlan}
        extendedPlanDetails={medicalPlan.extendedPlanDetails}
        enrollmentTags={onboardingPeriod.enrollmentTags}
        isDon={isDon}
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
      />
      {enrollmentIsDentalEnabled && (
        <>
          <EmployeePagePlansBenefitHeader
            benefitType={BenefitType.SUPPLEMENTAL_DENTAL}
            onboardingPeriodId={onboardingPeriodId}
            onAddPlanClick={() =>
              onEnrollmentTabActionChange(EnrollmentAction.ADD_DENTAL_PLAN)
            }
          />
          {dentalBenefits.length > 0 ? (
            dentalBenefits.map(
              ({ id, memberId, status, insuredPeople, plan }) => (
                <EmployeePagePlansBenefitCard
                  key={id}
                  benefitId={id}
                  memberId={memberId}
                  planId={medicalPlan.externalID}
                  onboardingPeriodId={onboardingPeriodId}
                  employerId={employerId}
                  employeeId={employeeId}
                  benefitType={BenefitType.SUPPLEMENTAL_DENTAL}
                  benefitsSummaryUrl={plan.benefitsSummaryUrl}
                  carrierName={plan.carrierName}
                  insuredPeople={insuredPeople}
                  selfEnroll={plan.selfEnroll}
                  planMarket={plan.planMarket}
                  policyType={plan.policyType}
                  premium={plan.premium}
                  name={plan.name}
                  status={status}
                  isDon={isDon}
                  onEnrollmentTabActionChange={onEnrollmentTabActionChange}
                />
              )
            )
          ) : (
            <EmployeePagePlansEmptyBenefitCard
              isDisabled={!isActive}
              label="Add a dental plan"
              onClick={() =>
                onEnrollmentTabActionChange(EnrollmentAction.ADD_DENTAL_PLAN)
              }
            />
          )}
        </>
      )}
      {enrollmentIsVisionEnabled && (
        <>
          <EmployeePagePlansBenefitHeader
            benefitType={BenefitType.SUPPLEMENTAL_VISION}
            onboardingPeriodId={onboardingPeriodId}
            onAddPlanClick={() =>
              onEnrollmentTabActionChange(EnrollmentAction.ADD_VISION_PLAN)
            }
          />
          {visionBenefits.length > 0 ? (
            visionBenefits.map(
              ({ id, memberId, status, insuredPeople, plan }) => (
                <EmployeePagePlansBenefitCard
                  key={id}
                  benefitId={id}
                  memberId={memberId}
                  planId={medicalPlan.externalID}
                  onboardingPeriodId={onboardingPeriodId}
                  employerId={employerId}
                  employeeId={employeeId}
                  benefitType={BenefitType.SUPPLEMENTAL_VISION}
                  benefitsSummaryUrl={plan.benefitsSummaryUrl}
                  carrierName={plan.carrierName}
                  insuredPeople={insuredPeople}
                  selfEnroll={plan.selfEnroll}
                  planMarket={plan.planMarket}
                  policyType={plan.policyType}
                  premium={plan.premium}
                  name={plan.name}
                  status={status}
                  isDon={isDon}
                  onEnrollmentTabActionChange={onEnrollmentTabActionChange}
                />
              )
            )
          ) : (
            <EmployeePagePlansEmptyBenefitCard
              isDisabled={!isActive}
              label="Add a vision plan"
              onClick={() =>
                onEnrollmentTabActionChange(EnrollmentAction.ADD_VISION_PLAN)
              }
            />
          )}
        </>
      )}
    </Stack>
  );
};
